import React from 'react';
import { Calendar, CalendarDays, UserCheck, UserX } from 'lucide-react';
import { getAttendanceRateClass, getAttendanceRateBgClass } from '../../utils/helpers';

/**
 * Component for displaying attendance summary statistics
 *
 * @param {Object} props
 * @param {Object} props.attendanceSummary - Summary data for attendance
 */
const AttendanceSummary = ({ attendanceSummary }) => {
    if (!attendanceSummary) {
        return (
            <div className="bg-white shadow rounded-lg overflow-hidden">
                <div className="px-4 py-5 sm:px-6 bg-gray-50">
                    <h2 className="text-lg leading-6 font-medium text-gray-900">Attendance Summary</h2>
                </div>
                <div className="p-6 text-center">
                    <Calendar className="h-10 w-10 text-gray-400 mx-auto mb-3"/>
                    <p className="text-sm text-gray-500">
                        No attendance records available yet
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-4 py-5 sm:px-6 bg-gray-50">
                <h2 className="text-lg leading-6 font-medium text-gray-900">Attendance Summary</h2>
            </div>
            <div className="px-4 py-5 sm:p-6">
                <div className="space-y-6">
                    <div>
                        <h3 className="text-base font-medium text-gray-900">Overall Attendance Rate</h3>
                        <div className="mt-2">
                            <div className="flex items-center">
                                <div className="w-full bg-gray-200 rounded-full h-2.5 mr-2">
                                    <div
                                        className={`h-2.5 rounded-full ${getAttendanceRateBgClass(attendanceSummary.attendance_rate)}`}
                                        style={{width: `${attendanceSummary.attendance_rate}%`}}
                                    ></div>
                                </div>
                                <span className={`text-sm font-medium ${getAttendanceRateClass(attendanceSummary.attendance_rate)}`}>
                                    {attendanceSummary.attendance_rate}%
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                        <div className="bg-blue-50 p-4 rounded-lg">
                            <div className="flex flex-col items-center">
                                <CalendarDays className="h-6 w-6 text-blue-600 mb-2"/>
                                <span className="text-xs font-medium text-blue-800">Total Days</span>
                                <span className="text-xl font-bold text-blue-900">{attendanceSummary.total_days}</span>
                            </div>
                        </div>

                        <div className="bg-green-50 p-4 rounded-lg">
                            <div className="flex flex-col items-center">
                                <UserCheck className="h-6 w-6 text-green-600 mb-2"/>
                                <span className="text-xs font-medium text-green-800">Present</span>
                                <span className="text-xl font-bold text-green-900">{attendanceSummary.present_days}</span>
                            </div>
                        </div>

                        <div className="bg-red-50 p-4 rounded-lg">
                            <div className="flex flex-col items-center">
                                <UserX className="h-6 w-6 text-red-600 mb-2"/>
                                <span className="text-xs font-medium text-red-800">Absent</span>
                                <span className="text-xl font-bold text-red-900">{attendanceSummary.absent_days}</span>
                            </div>
                        </div>
                    </div>

                    <div>
                        <h3 className="text-base font-medium text-gray-900 mb-2">Attendance Status</h3>
                        <div className="space-y-2">
                            <div className="flex justify-between items-center">
                                <div className="flex items-center">
                                    <div className="h-3 w-3 bg-green-500 rounded-full mr-2"></div>
                                    <span className="text-sm text-gray-600">Present</span>
                                </div>
                                <div className="flex items-center">
                                    <span className="text-sm font-medium text-gray-900">
                                        {attendanceSummary.present_days} days
                                    </span>
                                    <span className="ml-2 text-xs text-gray-500">
                                        ({Math.round((attendanceSummary.present_days / attendanceSummary.total_days) * 100) || 0}%)
                                    </span>
                                </div>
                            </div>

                            <div className="flex justify-between items-center">
                                <div className="flex items-center">
                                    <div className="h-3 w-3 bg-red-500 rounded-full mr-2"></div>
                                    <span className="text-sm text-gray-600">Absent</span>
                                </div>
                                <div className="flex items-center">
                                    <span className="text-sm font-medium text-gray-900">
                                        {attendanceSummary.absent_days} days
                                    </span>
                                    <span className="ml-2 text-xs text-gray-500">
                                        ({Math.round((attendanceSummary.absent_days / attendanceSummary.total_days) * 100) || 0}%)
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AttendanceSummary;